import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Save } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import OrganizationProfileService from '../../services/organizationProfileService';

const EMPTY_FORM = {
  authorityName: '',
  paymentTerms: '',
  defaultLocation: '',
  eligibilityCriteria: '',
  evaluationMethodology: '',
  standardClausesText: '',
};

const mapProfileToForm = (profile = {}) => ({
  authorityName: profile.authorityName || '',
  paymentTerms: profile.paymentTerms || '',
  defaultLocation: profile.defaultLocation || '',
  eligibilityCriteria: profile.eligibilityCriteria || '',
  evaluationMethodology: profile.evaluationMethodology || '',
  standardClausesText: Array.isArray(profile.standardClauses)
    ? profile.standardClauses.join('\n')
    : '',
});

const OrganizationProfileConfig = () => {
  const { success, error: showError, toasts, removeToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // showError/success from useToast are new function refs each render — do not
  // put them in useCallback deps or useEffect([loadProfile]) will loop forever.
  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await OrganizationProfileService.getProfile();
      setForm(mapProfileToForm(res?.data || {}));
    } catch {
      showError('Failed to load organization profile.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast helpers are unstable refs
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const onSave = async () => {
    const payload = {
      authorityName: form.authorityName.trim(),
      paymentTerms: form.paymentTerms.trim(),
      defaultLocation: form.defaultLocation.trim(),
      eligibilityCriteria: form.eligibilityCriteria.trim(),
      evaluationMethodology: form.evaluationMethodology.trim(),
      standardClauses: form.standardClausesText
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean),
    };

    setSaving(true);
    try {
      await OrganizationProfileService.saveProfile(payload);
      success('Organization profile saved.');
      await loadProfile();
    } catch (err) {
      showError(err?.message || 'Failed to save organization profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Organization Profile"
        description="Configure tenant-wide sourcing defaults used when creating new RFPs."
        actions={[
          {
            icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />,
            variant: 'outline',
            onClick: loadProfile,
          },
          {
            label: 'Save',
            icon: <Save size={16} />,
            variant: 'primary',
            onClick: onSave,
            disabled: saving,
          },
        ]}
      />

      <div className="page-container">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Authority Name
              </label>
              <input
                value={form.authorityName}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, authorityName: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                placeholder="Office of the ..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Default Location
              </label>
              <input
                value={form.defaultLocation}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, defaultLocation: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                placeholder="Default jurisdiction or geography"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
              Payment Terms
            </label>
            <textarea
              value={form.paymentTerms}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, paymentTerms: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[90px]"
              placeholder="Define standard payment terms"
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
              Eligibility Criteria
            </label>
            <textarea
              value={form.eligibilityCriteria}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, eligibilityCriteria: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[100px]"
              placeholder="Enter baseline bidder eligibility criteria"
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
              Evaluation Methodology
            </label>
            <textarea
              value={form.evaluationMethodology}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  evaluationMethodology: e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[90px]"
              placeholder="For example: QCBS 70:30"
            />
          </div>

          <div className="mb-2">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
              Standard Clauses (One Per Line)
            </label>
            <textarea
              value={form.standardClausesText}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, standardClausesText: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[120px]"
              placeholder="Clause 1&#10;Clause 2&#10;Clause 3"
            />
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default OrganizationProfileConfig;
